import { getConfig } from './config';

/**
 * Race-director sign-in against Cognito's JSON API directly (no SDK needed).
 * Tokens live in localStorage; the access token is refreshed shortly before
 * it expires.
 */

interface Tokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAtMs: number;
  email: string;
}

const KEY = 'splitline.director';

export type SignInResult = { kind: 'signed-in' } | { kind: 'new-password'; session: string };

async function cognito<T>(action: string, body: object): Promise<T> {
  const { region } = getConfig();
  const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-amz-json-1.1', 'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}` },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw Object.assign(new Error(json.message ?? 'Sign-in failed'), { code: String(json.__type ?? '').split('#').pop() });
  return json as T;
}

interface AuthResult {
  AuthenticationResult?: { AccessToken: string; IdToken: string; RefreshToken?: string; ExpiresIn: number };
  ChallengeName?: string;
  Session?: string;
}

function save(email: string, r: NonNullable<AuthResult['AuthenticationResult']>, refreshToken?: string) {
  const tokens: Tokens = {
    accessToken: r.AccessToken,
    idToken: r.IdToken,
    refreshToken: r.RefreshToken ?? refreshToken ?? '',
    expiresAtMs: Date.now() + r.ExpiresIn * 1000,
    email,
  };
  localStorage.setItem(KEY, JSON.stringify(tokens));
}

function load(): Tokens | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const { userPoolClientId } = getConfig();
  const res = await cognito<AuthResult>('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: userPoolClientId,
    AuthParameters: { USERNAME: email, PASSWORD: password },
  });
  if (res.ChallengeName === 'NEW_PASSWORD_REQUIRED' && res.Session) return { kind: 'new-password', session: res.Session };
  if (!res.AuthenticationResult) throw new Error(`Unsupported sign-in step: ${res.ChallengeName ?? 'unknown'}`);
  save(email, res.AuthenticationResult);
  return { kind: 'signed-in' };
}

export async function setNewPassword(email: string, newPassword: string, session: string): Promise<void> {
  const { userPoolClientId } = getConfig();
  const res = await cognito<AuthResult>('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: userPoolClientId,
    Session: session,
    ChallengeResponses: { USERNAME: email, NEW_PASSWORD: newPassword },
  });
  if (!res.AuthenticationResult) throw new Error('Could not set the new password');
  save(email, res.AuthenticationResult);
}

export function signOut() {
  localStorage.removeItem(KEY);
}

export function currentEmail(): string | null {
  if (getConfig().authMode === 'dev') return localStorage.getItem('splitline.devUser') ?? 'dev-user';
  return load()?.email ?? null;
}

export function isSignedIn(): boolean {
  return getConfig().authMode === 'dev' || load() !== null;
}

let refreshing: Promise<string | null> | null = null;

/** A valid access token, refreshing it if it expires within a minute; null when signed out. */
export async function getAccessToken(): Promise<string | null> {
  const tokens = load();
  if (!tokens) return null;
  if (tokens.expiresAtMs - Date.now() > 60_000) return tokens.accessToken;
  refreshing ??= (async () => {
    try {
      const res = await cognito<AuthResult>('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: getConfig().userPoolClientId,
        AuthParameters: { REFRESH_TOKEN: tokens.refreshToken },
      });
      if (!res.AuthenticationResult) throw new Error('refresh failed');
      save(tokens.email, res.AuthenticationResult, tokens.refreshToken);
      return res.AuthenticationResult.AccessToken;
    } catch {
      signOut();
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** Emails a reset code to a director who forgot their password. */
export async function forgotPassword(email: string): Promise<void> {
  await cognito('ForgotPassword', { ClientId: getConfig().userPoolClientId, Username: email });
}

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  await cognito('ConfirmForgotPassword', { ClientId: getConfig().userPoolClientId, Username: email, ConfirmationCode: code, Password: newPassword });
}

/** Self sign-up: Cognito emails a code to confirm the address. */
export async function signUp(email: string, password: string): Promise<void> {
  await cognito('SignUp', {
    ClientId: getConfig().userPoolClientId,
    Username: email,
    Password: password,
    UserAttributes: [{ Name: 'email', Value: email }],
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  await cognito('ConfirmSignUp', { ClientId: getConfig().userPoolClientId, Username: email, ConfirmationCode: code });
}

export async function resendSignUpCode(email: string): Promise<void> {
  await cognito('ResendConfirmationCode', { ClientId: getConfig().userPoolClientId, Username: email });
}
